import type { Language } from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { createContext, type ReactNode, useContext } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'

const fragments = {
  UserProvider_member: graphql(`
    fragment UserProvider_member on Member {
      id
      isPartner
      language
      country
      sso
      name
      email
      phone
      role
      createdAt
      profileImageUrl
      partner {
        id
        verificationStatus
        institutions {
          id
        }
      }
      keys {
        id
        privateKey
        publicKey
        partnerPermissions {
          id
          type
          enabled
          motherId
        }
      }
      hasAccessToMultipleInstitutions
      motherImplicit {
        id
        type
        country
        config {
          id
          institutionName
        }
      }
      partnerCompanyIds
      partnerInstitutionIds
    }
  `),
}

export type ApplicationType = 'akutan' | 'kredenc' | 'follow-up'

export type UserContextProps = {
  logout: () => void
  data: {
    id: string
  }
  // TODO: Simplify and abstractize viewer
  viewer?: ResultOf<typeof fragments.UserProvider_member> | null
} & (
  | {
      // It shouldn't be here, but we need it for now (otherwise we should create some App package and move it there)
      application: Extract<ApplicationType, 'akutan' | 'kredenc'> | null
    }
  | {
      application: Extract<ApplicationType, 'follow-up'>
    }
)

export const UserContext = createContext<UserContextProps>({
  logout: () => undefined,
  viewer: null,
  application: null,
  data: {
    id: '',
  },
})

type UserProviderProps = {
  onLogout: () => void
  onChangeLanguage: (language: Language) => void
  member: FragmentType<typeof fragments.UserProvider_member> | null
  children: ReactNode
} & (
  | {
      application: Extract<ApplicationType, 'akutan' | 'kredenc'>
      userId?: never
    }
  | {
      // for now because follow-up is not using UserProvider globally
      application: Extract<ApplicationType, 'follow-up'>
      userId: string
    }
)

export const UserProvider = ({
  children,
  application,
  userId,
  member: _member,
}: UserProviderProps) => {
  const member = getFragmentData(fragments.UserProvider_member, _member)

  // for now because follow-up is not using UserProvider globally
  if (application === 'follow-up') {
    return (
      <UserContext.Provider
        value={{
          logout: () => null,
          application,
          viewer: null,
          data: {
            id: userId,
          },
        }}
      >
        {children}
      </UserContext.Provider>
    )
  }

  return (
    <UserContext.Provider
      value={{
        application,
        logout: () => {
          throw new Error('Implement logout in UserProvider')
        },
        viewer: member,
        data: {
          id: member?.id ?? '',
        },
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => useContext(UserContext)
